import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Stethoscope, Users, ClipboardList, FileText, AlertCircle, ChevronRight } from "lucide-react";

export default function ReferrerStats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard/by-referrer")
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], unmatched: [], totals: {} }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card className="p-6 border-[#E5E5E3] bg-white" data-testid="referrer-stats-card">
        <div className="text-sm text-[#8A8A86]">医療機関別集計を読み込み中…</div>
      </Card>
    );
  }

  const items = (data?.items || []).filter((x) => x.patient_count > 0);
  const unmatched = data?.unmatched || [];
  const totals = data?.totals || {};

  return (
    <Card className="p-6 border-[#E5E5E3] bg-white" data-testid="referrer-stats-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#A74141]/10 flex items-center justify-center">
            <Stethoscope size={18} className="text-[#A74141]" strokeWidth={1.5} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#1C1C1A]">医療機関別 集計</h2>
            <div className="text-xs text-[#8A8A86] font-numeric tracking-wider">
              紹介元・主治医ごとの利用者・活動 (過去30日)
            </div>
          </div>
        </div>
        <button
          data-testid="manage-referrers-button"
          onClick={() => nav("/referrers")}
          className="text-sm text-[#2C5545] hover:underline"
        >
          医療機関マスタ →
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-5 pb-4 border-b border-[#E5E5E3]">
        <Mini label="登録医療機関" value={totals.referrers ?? 0} testid="ref-stat-referrers" />
        <Mini label="関連付け済利用者" value={totals.patients_with_institution ?? 0} testid="ref-stat-with-inst" />
        <Mini label="未関連付け" value={totals.patients_without_institution ?? 0} testid="ref-stat-without-inst" highlight={!!totals.patients_without_institution} />
        <Mini label="登録外医療機関" value={unmatched.length} testid="ref-stat-unmatched" highlight={!!unmatched.length} />
      </div>

      {items.length === 0 && unmatched.length === 0 ? (
        <div className="py-8 text-center text-[#8A8A86] text-sm">
          利用者と医療機関の関連付けがまだありません。
          <br />
          利用者の編集画面から「医療機関マスタ」を選択して紐付けできます。
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <div
              key={it.referrer_id}
              data-testid={`ref-row-${it.referrer_id}`}
              className="flex items-center gap-3 p-3 rounded-lg border border-[#E5E5E3] hover:border-[#2C5545]/40 hover:bg-[#FBFBF9] transition cursor-pointer"
              onClick={() => nav("/referrers")}
            >
              <div className="w-9 h-9 rounded-full bg-[#2C5545]/10 flex items-center justify-center shrink-0">
                <Stethoscope size={14} className="text-[#2C5545]" strokeWidth={1.7} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm truncate">{it.referrer_name}</span>
                  {it.kind && (
                    <Badge variant="outline" className="text-[10px] border-[#2C5545]/30 text-[#2C5545]">
                      {it.kind}
                    </Badge>
                  )}
                </div>
                <div className="text-[11px] text-[#8A8A86] truncate">
                  {it.person_in_charge || "—"}
                  {it.patient_names?.length > 0 && (
                    <> · {it.patient_names.slice(0, 3).join(" / ")}{it.patient_count > 3 ? ` 他${it.patient_count - 3}名` : ""}</>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs font-numeric shrink-0">
                <Stat icon={Users} label="利用者" value={it.patient_count} color="#2C5545" />
                <Stat icon={ClipboardList} label="記録" value={it.recent_records_30d} color="#3A6B52" />
                <Stat icon={FileText} label="書類" value={it.recent_docs_30d} color="#6B5B4D" />
              </div>
              <ChevronRight size={14} className="text-[#C5C5C2]" />
            </div>
          ))}

          {unmatched.length > 0 && (
            <div className="mt-4 pt-3 border-t border-dashed border-[#D4A373]/40">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle size={14} className="text-[#8B6424]" />
                <span className="text-xs font-bold text-[#8B6424]">未登録の医療機関 ({unmatched.length})</span>
                <span className="text-[10px] text-[#8A8A86]">
                  · マスタに登録すると紹介元として再利用できます
                </span>
              </div>
              <div className="space-y-1.5">
                {unmatched.map((it, i) => (
                  <div
                    key={i}
                    data-testid={`ref-unmatched-${i}`}
                    className="flex items-center gap-3 p-2 rounded-lg bg-[#FFF8E6]/60 border border-[#D4A373]/30"
                  >
                    <AlertCircle size={12} className="text-[#8B6424] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{it.referrer_name}</div>
                      <div className="text-[10px] text-[#8A8A86] truncate">
                        {it.patient_names?.slice(0, 3).join(" / ")}
                        {it.patient_count > 3 ? ` 他${it.patient_count - 3}名` : ""}
                      </div>
                    </div>
                    <span className="text-[11px] font-numeric text-[#8B6424]">{it.patient_count}名</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function Mini({ label, value, testid, highlight }) {
  return (
    <div
      data-testid={testid}
      className={`p-2.5 rounded-lg border ${highlight ? "border-[#D4A373]/50 bg-[#FFF8E6]/60" : "border-[#E5E5E3] bg-[#FBFBF9]"}`}
    >
      <div className="text-[10px] tracking-wider uppercase text-[#8A8A86]">{label}</div>
      <div className={`text-xl font-bold font-numeric ${highlight ? "text-[#8B6424]" : "text-[#1C1C1A]"}`}>{value}</div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="flex items-center gap-1" title={label}>
      <Icon size={12} style={{ color }} strokeWidth={1.7} />
      <span style={{ color }} className="font-bold">{value}</span>
    </div>
  );
}
